"use client";

import { useState, type FormEvent } from "react";
import Link from "next/link";
import { ArrowUpRight, Check, Mail } from "lucide-react";

type State = "idle" | "submitting" | "done";

export function ContactForm() {
  const [state, setState] = useState<State>("idle");

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setState("submitting");
    setTimeout(() => setState("done"), 900);
  };

  if (state === "done") {
    return (
      <div
        role="status"
        aria-live="polite"
        className="rounded-lg border border-accent/40 p-7 lg:p-10"
        style={{ backgroundColor: "#111111" }}
      >
        <div className="flex items-start gap-5">
          <span
            aria-hidden="true"
            className="checkmark-badge inline-flex size-14 items-center justify-center rounded-full bg-accent text-accent-ink shrink-0"
            style={{ boxShadow: "0 0 0 6px rgba(191,247,69,0.18)" }}
          >
            <Check className="size-7 checkmark-tick" strokeWidth={3} />
          </span>
          <div>
            <p className="text-small font-semibold uppercase tracking-[0.2em] text-accent">
              Message sent
            </p>
            <h2 className="text-h3 mt-2">
              Message sent! We'll reply within the working day.
            </h2>
            <p className="mt-3 text-body text-text-muted max-w-lg">
              Average reply time is under 2 hours during UK business hours.
              Keep an eye on your inbox (and the spam folder, just in case).
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                href="/services"
                className="inline-flex items-center gap-2 rounded-pill border border-border-strong px-5 py-2.5 text-small font-semibold text-text transition hover:border-accent hover:text-accent"
              >
                Browse services while you wait
                <ArrowUpRight className="size-4" />
              </Link>
              <button
                type="button"
                onClick={() => setState("idle")}
                className="text-small text-text-muted hover:text-accent transition-colors underline-offset-4 hover:underline"
              >
                Send another message
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const submitting = state === "submitting";

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <FloatField name="name" label="Name" required disabled={submitting} />
        <FloatField name="email" type="email" label="Email" required disabled={submitting} />
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <FloatField name="phone" type="tel" label="Phone (optional)" disabled={submitting} />
        <FloatField name="company" label="Brand / company (optional)" disabled={submitting} />
      </div>
      <FloatSelect
        name="service"
        label="Service you're interested in"
        disabled={submitting}
        options={[
          { value: "clothing-brand-startup", label: "Clothing Brand Startup (pillar)" },
          { value: "logo-branding", label: "Logo Branding & Identity" },
          { value: "seo", label: "SEO for Clothing Brands" },
          { value: "content-writing", label: "Content Writing" },
          { value: "not-sure", label: "Not sure yet" },
        ]}
      />
      <FloatTextarea
        name="message"
        label="Where are you at with your brand?"
        required
        disabled={submitting}
      />

      {/* Honeypot */}
      <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

      <button
        type="submit"
        disabled={submitting}
        className="group inline-flex items-center gap-2 rounded-pill bg-accent px-7 py-3.5 font-bold text-accent-ink transition-all duration-300 hover:bg-accent-hover hover:-translate-y-0.5 disabled:opacity-80 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        style={
          submitting
            ? {}
            : { boxShadow: "0 12px 32px -10px rgba(191,247,69,0.55)" }
        }
      >
        {submitting ? (
          <>
            <span
              aria-hidden="true"
              className="inline-block size-4 rounded-full border-2 border-black/30 border-t-black animate-spin"
            />
            Sending…
          </>
        ) : (
          <>
            Send message
            <ArrowUpRight className="size-5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </>
        )}
      </button>
    </form>
  );
}

/* ───────── Floating-label primitives ───────── */

function FloatField({
  name,
  label,
  type = "text",
  required,
  disabled,
}: {
  name: string;
  label: string;
  type?: string;
  required?: boolean;
  disabled?: boolean;
}) {
  const [value, setValue] = useState("");
  return (
    <FieldWrap label={label} required={required} hasValue={!!value}>
      <input
        type={type}
        name={name}
        required={required}
        disabled={disabled}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="field-input"
        placeholder=" "
        autoComplete="off"
      />
    </FieldWrap>
  );
}

function FloatTextarea({
  name,
  label,
  required,
  disabled,
  placeholder,
}: {
  name: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [value, setValue] = useState("");
  return (
    <FieldWrap label={label} required={required} hasValue={!!value} multiline>
      <textarea
        name={name}
        required={required}
        disabled={disabled}
        rows={6}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder ?? " "}
        className="field-input field-textarea"
      />
    </FieldWrap>
  );
}

function FloatSelect({
  name,
  label,
  disabled,
  options,
}: {
  name: string;
  label: string;
  disabled?: boolean;
  options: Array<{ value: string; label: string }>;
}) {
  const [value, setValue] = useState("");
  return (
    <FieldWrap label={label} hasValue={!!value}>
      <select
        name={name}
        disabled={disabled}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="field-input field-select"
      >
        <option value="" />
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </FieldWrap>
  );
}

function FieldWrap({
  label,
  required,
  hasValue,
  multiline,
  children,
}: {
  label: string;
  required?: boolean;
  hasValue: boolean;
  multiline?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className={["field-wrap", hasValue ? "is-filled" : ""].join(" ")}>
      <span className="field-label">
        {label}
        {required && <span className="text-accent ml-1">*</span>}
      </span>
      {children}

      <style jsx>{`
        .field-wrap {
          position: relative;
          display: block;
        }
        .field-label {
          position: absolute;
          left: 1rem;
          top: ${multiline ? "1rem" : "50%"};
          transform: ${multiline ? "translateY(0)" : "translateY(-50%)"};
          font-size: 0.9375rem;
          color: #a0a0a0;
          pointer-events: none;
          background-color: transparent;
          padding: 0 0.35rem;
          transition: top 200ms ease, font-size 200ms ease, color 200ms ease,
            transform 200ms ease;
          z-index: 1;
        }
        .field-wrap.is-filled .field-label,
        .field-wrap:focus-within .field-label {
          top: -0.45rem;
          font-size: 0.6875rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #bff745;
          background-color: #111111;
          transform: translateY(0);
        }
        .field-wrap :global(.field-input) {
          display: block;
          width: 100%;
          background-color: #111111;
          color: #ffffff;
          border: 1px solid #1e1e1e;
          border-radius: 0.5rem;
          padding: 1.3rem 1.1rem;
          font-size: 1rem;
          transition: border-color 200ms ease, box-shadow 200ms ease,
            border-bottom-color 200ms ease;
          appearance: none;
        }
        .field-wrap :global(.field-input):focus {
          outline: none;
          border-color: #1e1e1e;
          border-bottom: 2px solid #bff745;
          padding-bottom: calc(1.3rem - 1px);
          box-shadow: 0 4px 0 -2px rgba(191, 247, 69, 0.0);
        }
        .field-wrap :global(.field-input):disabled {
          opacity: 0.6;
        }
        .field-wrap :global(.field-textarea) {
          resize: vertical;
          min-height: 9rem;
        }
        .field-wrap :global(.field-select) {
          cursor: pointer;
          background-image: linear-gradient(45deg, transparent 50%, #bff745 50%),
            linear-gradient(135deg, #bff745 50%, transparent 50%);
          background-position: calc(100% - 18px) 50%,
            calc(100% - 12px) 50%;
          background-size: 6px 6px, 6px 6px;
          background-repeat: no-repeat;
          padding-right: 2.5rem;
        }
      `}</style>
    </label>
  );
}
